import { apiRequest } from "./client";

export interface ApiQuestion {
  id: string;
  stem: string;
  options: { id: string; text: string }[] | Record<string, string> | null;
  questionType: string;
  marks: number | null;
  correctAnswer: string | null;
  explanation: string | null;
  topicId: string | null;
  /** The topic's name, so nothing downstream has to resolve an id. */
  topicName?: string | null;
  difficultyLevel: number | null;
  examYear: number | null;
}

export interface TrialState {
  used: number;
  remaining: number;
  exhausted: boolean;
  /** The server's call on when the countdown chip should appear. */
  shouldWarn: boolean;
  limit: number;
}

export interface StartedSession {
  sessionId: string;
  subjectId: string;
  total: number;
  questions: ApiQuestion[];
  /** Null for a student with full access — there is nothing to count down. */
  trial?: TrialState | null;
  /** Theory sessions only: whether this subject has a WAEC marking scheme. */
  theoryMarkable?: boolean;
  /**
   * Readiness for this subject as it stood before the session started.
   *
   * Null when there is no evidence yet. Captured at start because by the time
   * the summary renders the engine has already moved the number, so this is
   * the only moment a real before/after can be taken.
   */
  readinessBefore?: number | null;
}

export interface SubmittedSession {
  sessionId: string;
  status: string;
  totalQuestions: number;
  totalCorrectAnswers: number;
  score: number | null;
  completedAt: string | null;
}

export interface StartPracticeOptions {
  subjectId: string;
  mode?: "by_year" | "by_topic" | "by_level" | "weak_area" | "spaced_repetition";
  topicIds?: string[];
  examYear?: number;
  count?: number;
  paper?: "objective" | "theory";
}

export function startPractice(options: StartPracticeOptions): Promise<StartedSession> {
  return apiRequest<StartedSession>("/api/practice/sessions", {
    method: "POST",
    body: options,
    authenticated: true,
  });
}

/**
 * Submit the whole session at once.
 *
 * `idempotencyKey` is what makes an offline replay safe: a session answered on
 * a bus and sent twice must score once. The caller generates it when the
 * session starts, not when it sends, so a retry carries the same key.
 */
export function submitPractice(
  sessionId: string,
  answers: { questionId: string; answer: string | null; timeTakenSeconds?: number }[],
  idempotencyKey?: string,
): Promise<SubmittedSession> {
  return apiRequest<SubmittedSession>(`/api/practice/sessions/${sessionId}/submit`, {
    method: "POST",
    body: { answers, ...(idempotencyKey ? { idempotencyKey } : {}) },
    authenticated: true,
  });
}

/**
 * Send a session that was answered with no network.
 *
 * Separate from `submitPractice` because there is no server session id: the
 * student practised from a downloaded pack, so the session is created from the
 * answers on arrival. The key is required — it is the only thing that stops a
 * flaky reconnect scoring one sitting twice.
 */
export function replayOfflineSession(
  subjectId: string,
  answers: { questionId: string; answer: string | null; timeTakenSeconds?: number }[],
  idempotencyKey: string,
  examType?: "JAMB" | "WAEC" | null,
): Promise<SubmittedSession> {
  return apiRequest<SubmittedSession>("/api/practice/sessions/offline", {
    method: "POST",
    // The exam it was practised in: the app may have switched since.
    body: { subjectId, answers, idempotencyKey, ...(examType ? { examType } : {}) },
    authenticated: true,
  });
}
